import React from "react";
import { Link } from "react-router-dom";
import { servicesData } from "../data/mockData";
import { ArrowRight, Star, Zap, ShoppingCart, Radio, BarChart3 } from "lucide-react";

const serviceIcons = {
  "amazon-ads": Star,
  "performance-engine": BarChart3,
  "marketplace-expansion": ShoppingCart,
  "quick-commerce": Zap,
  "programmatic-advertising": Radio,
};

const serviceColors = {
  "amazon-ads": "#FF9900",
  "performance-engine": "#4285F4",
  "marketplace-expansion": "#34A853",
  "quick-commerce": "#EA4335",
  "programmatic-advertising": "#9B59B6",
};

const Services = () => {
  return (
    <div className="overflow-hidden">

      {/* ── Header ── */}
      <section className="pt-32 pb-8 md:pt-40 md:pb-12">
        <div className="max-w-[1320px] mx-auto px-6">
          <p
            className="text-[#10B981] text-[15px] font-semibold mb-2"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Urban Hunter
          </p>
          <h1
            className="text-[40px] md:text-[56px] font-bold leading-tight mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
          >
            Our Services
          </h1>
          <p
            className="text-[#555] text-[16px] max-w-[600px] leading-relaxed"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            From Amazon to quick commerce we build data driven growth systems
            that scale brands profitably across every major platform.
          </p>
        </div>
      </section>

      {/* ── Services Grid ── */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service) => {
              const IconComp = serviceIcons[service.id] || BarChart3;
              const accentColor = serviceColors[service.id] || "#10B981";

              return (
                <Link
                  key={service.id}
                  to={service.link}
                  className="group bg-white border border-gray-100 rounded-2xl p-8 no-underline hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
                >
                  {/* Icon or Logo */}
                  {service.logo ? (
                    <div className="mb-6 h-[44px] flex items-center">
                      <img
                        src={service.logo}
                        alt={service.title}
                        className="h-full object-contain"
                        style={{ maxWidth: "160px" }}
                      />
                    </div>
                  ) : (
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-colors duration-300"
                      style={{ background: `${accentColor}18` }}
                    >
                      <IconComp
                        size={24}
                        style={{ color: accentColor }}
                        className="group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}

                  <h3
                    className="text-[20px] font-bold mb-3"
                    style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                  >
                    {service.title}
                  </h3>
                  <p
                    className="text-[#555] text-[14px] leading-relaxed mb-6 flex-1"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {service.shortDescription}
                  </p>
                  <div
                    className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider group-hover:gap-3 transition-all duration-300"
                    style={{ color: accentColor }}
                  >
                    Learn More <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Why Urban Hunter strip ── */}
      <section className="py-16 bg-[#F8F6FF]">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              { stat: "20+", label: "Global Marketplaces" },
              { stat: "100%", label: "Data-Driven Decisions" },
              { stat: "US · UK · IN", label: "Markets We Operate In" },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm">
                <p
                  className="text-[40px] font-bold text-[#10B981] mb-2"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {item.stat}
                </p>
                <p
                  className="text-[#555] text-[15px]"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-20" style={{ background: "#1B1B3A" }}>
        <div className="max-w-[1320px] mx-auto px-6 text-center">
          <p
            className="text-[#10B981] text-[14px] font-semibold uppercase tracking-widest mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Ready to Scale?
          </p>
          <h2
            className="text-[28px] md:text-[40px] font-bold mb-4 text-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            If you're ready to scale with precision, let's talk.
          </h2>
          <p
            className="text-white/70 text-[16px] mb-10 max-w-[560px] mx-auto leading-relaxed"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Get in touch with our team and discover how Urban Hunter can build
            your growth engine from the ground up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/free-audit"
              className="inline-flex items-center justify-center gap-2 bg-[#10B981] text-white px-8 py-4 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline hover:bg-[#059669] transition-colors duration-300"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Get a Free Growth Audit
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-white/20 px-8 py-4 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline hover:bg-white/20 transition-colors duration-300"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Speak With a Strategy Expert
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Services;
